import { InformationCircleIcon } from '@heroicons/react/24/outline';
import Tooltip from 'antd/es/tooltip';

import { formatDecimalAmount } from 'utils/formatters/formatCurrency';

import type { BatchDraft } from 'api';

import type { FindAccountByAccountNumber } from 'hooks/context/useVirtualAccounts';

import { DetailsDraftAttachmentSectionContainer } from '@atoms/DetailsAttachmentSection/DetailsDraftAttachmentSectionContainer';
import { DetailsItem } from '@atoms/DetailsItem/DetailsItem';
import { FormattedDate } from '@atoms/FormattedDate/FormattedDate';

import { CopyToClipboardButton } from '@molecules/CopyToClipboardButton';
import { Country } from '@molecules/Country';

import type { CollapseItemWithTypedSection } from './BatchPayoutDraftDetails';

export const getCollapseItemsFromBatchDraft = (
  payoutData: BatchDraft,
  payoutId: number,
  findAccountByAccountNumber: FindAccountByAccountNumber
): CollapseItemWithTypedSection[] => {
  const sections: CollapseItemWithTypedSection[] = [];
  const account = findAccountByAccountNumber(payoutData.debtor_account?.id);

  sections.push({
    key: 'payout-request',
    label: (
      <div data-testid="batch-draft-details-header-payout-request">
        Payout Request
      </div>
    ),
    children: (
      <div className="px-2">
        <DetailsItem
          title="Account"
          item={
            <div>
              <div>{account?.name}</div>
              <div className="text-xs font-normal">
                {account?.account_number}
              </div>
            </div>
          }
          data-testid="batch-draft-details-account"
        />
        <DetailsItem
          title="End-to-end ID"
          item={
            <CopyToClipboardButton
              text={payoutData.end_to_end_id?.toUpperCase()}
            />
          }
          data-testid="batch-draft-details-e2e-id"
        />
        <DetailsItem
          title="Amount"
          item={
            <div>
              <div>{formatDecimalAmount(Number(payoutData.amount))}</div>
              <div className="text-xs font-normal">
                {payoutData.currency_code}
              </div>
            </div>
          }
          data-testid="batch-draft-details-amount"
        />
        <DetailsItem
          title="Country"
          item={
            <Country
              countryIso={payoutData.creditor_account?.country_code ?? ''}
            />
          }
          data-testid="batch-draft-details-destination-country"
        />
        <DetailsItem
          title="Selected Product"
          item={payoutData.local_instrument}
          data-testid="batch-draft-details-selected-product"
        />
      </div>
    )
  });

  sections.push({
    key: 'sender',
    label: <div>Sender</div>,
    children: (
      <div className="px-2">
        <DetailsItem
          title="Name"
          item={payoutData.ultimate_debtor?.name}
          data-testid="batch-draft-details-ultimate-debtor-name"
        />
        <DetailsItem
          title="Address lines"
          item={payoutData.ultimate_debtor?.address_lines}
          data-testid="batch-draft-details-ultimate-debtor-address"
        />
        <DetailsItem
          title="City"
          item={payoutData.ultimate_debtor?.city}
          data-testid="batch-draft-details-ultimate-debtor-city"
        />
        <DetailsItem
          title="Post code"
          item={payoutData.ultimate_debtor?.postcode}
          data-testid="batch-draft-details-ultimate-debtor-postcode"
        />
        <DetailsItem
          title="Country"
          item={
            <Country
              countryIso={payoutData.ultimate_debtor?.country_code ?? ''}
            />
          }
          data-testid="batch-draft-details-ultimate-debtor-country"
        />
      </div>
    )
  });

  sections.push({
    key: 'receiver-bank',
    label: <div>Receiver Bank</div>,
    children: (
      <div className="px-2">
        <DetailsItem
          title="IBAN"
          item={
            <CopyToClipboardButton text={payoutData.creditor_account?.iban} />
          }
          data-testid="batch-draft-details-receiver-bank-iban"
        />
        <DetailsItem
          title="Country"
          item={
            <Country
              countryIso={payoutData.creditor_account?.country_code ?? ''}
            />
          }
          data-testid="batch-draft-details-receiver-bank-country"
        />
        <DetailsItem
          title="Account type"
          item={payoutData.creditor_account?.type}
          data-testid="batch-draft-details-receiver-bank-type"
        />
        <DetailsItem
          title="Account IBAN"
          item={payoutData.creditor_account?.iban}
          data-testid="batch-draft-details-receiver-bank-iban-repeat"
        />
      </div>
    )
  });

  sections.push({
    key: 'receiver',
    label: <div>Receiver</div>,
    children: (
      <div className="px-2">
        <DetailsItem
          title="Name"
          item={payoutData.creditor?.name}
          data-testid="batch-draft-details-receiver-name"
        />
        <DetailsItem
          title="Date of Birth"
          item={
            payoutData.creditor?.birthdate ? (
              <FormattedDate
                value={payoutData.creditor.birthdate}
                className="font-semibold"
              />
            ) : undefined
          }
          data-testid="batch-draft-details-receiver-dob"
        />
        <DetailsItem
          title="Address"
          item={payoutData.creditor?.address_lines}
          data-testid="batch-draft-details-receiver-address"
        />
        <DetailsItem
          title="Postal Code"
          item={payoutData.creditor?.postcode}
          data-testid="batch-draft-details-receiver-postcode"
        />
        <DetailsItem
          title="City"
          item={payoutData.creditor?.city}
          data-testid="batch-draft-details-receiver-city"
        />
        <DetailsItem
          title="Country"
          item={
            <Country countryIso={payoutData.creditor?.country_code ?? ''} />
          }
          data-testid="batch-draft-details-receiver-country"
        />
      </div>
    )
  });

  sections.push({
    key: 'attachments',
    label: (
      <div className="flex items-center gap-1">
        Attachments
        <Tooltip
          title="This feature is not intended for RFIs"
          placement="right"
          arrow={true}
        >
          <InformationCircleIcon className="size-4 stroke-current" />
        </Tooltip>
      </div>
    ),
    children: <DetailsDraftAttachmentSectionContainer resourceId={payoutId} />
  });

  return sections;
};
